import { useEffect, useRef, useState } from 'react';

const reasons = [
  {
    icon: 'ri-flask-line',
    title: 'Ciencia reproducible',
    desc: 'Para sistemas vivos complejos. Cada análisis es trazable, documentado y verificable.',
  },
  {
    icon: 'ri-file-check-line',
    title: 'Rigor científico',
    desc: 'Metodologías verificables y criterios técnicos sólidos en cada proyecto.',
  },
  {
    icon: 'ri-mind-map',
    title: 'Pensamiento sistémico',
    desc: 'Enfoque integrador para resolver problemas reales con modelos computacionales robustos.',
  },
  {
    icon: 'ri-exchange-line',
    title: 'Integración multidisciplinaria',
    desc: 'Conectamos academia, industria y gobierno para generar soluciones estratégicas.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Evidencia y tecnología',
    desc: 'Soluciones respaldadas por evidencia científica y tecnología de vanguardia.',
  },
];

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setInView(true);
    }, { threshold });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function WhyChooseSection() {
  const { ref: sectionRef, inView } = useInView(0.1);

  return (
    <section
      id="why-choose"
      ref={sectionRef}
      className="relative py-20 md:py-32 bg-biodata-dark overflow-hidden"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-radial from-biodata-teal/5 via-transparent to-transparent pointer-events-none" />

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        {/* Header */}
        <div
          className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-biodata-teal/10 border border-biodata-teal/20 mb-4">
            <span className="text-xs text-biodata-teal font-medium uppercase tracking-wider">Propuesta de valor</span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            ¿Por qué elegir <span className="text-gradient">ANALITICA?</span>
          </h2>
          <p className="text-biodata-muted text-base md:text-lg leading-relaxed">
            ANALITICA ofrece soluciones científicas reproducibles, robustas e integradoras 
            para problemas complejos en sistemas vivos. Combina bioinformática, ciencia de datos, 
            biología de sistemas y tecnología con una visión humanística.
          </p>
        </div>

        {/* Reasons Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {reasons.map((reason, index) => (
            <div
              key={reason.title}
              className={`glass-card rounded-xl p-6 md:p-8 group hover:border-biodata-teal/40 transition-all duration-500 ${
                inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="w-12 h-12 rounded-xl bg-biodata-teal/10 flex items-center justify-center mb-5 group-hover:bg-biodata-teal/20 transition-colors">
                <i className={`${reason.icon} text-xl text-biodata-teal`} />
              </div>
              <h3 className="text-lg font-display font-semibold text-white mb-3 group-hover:text-biodata-teal transition-colors">
                {reason.title}
              </h3>
              <p className="text-biodata-muted text-sm leading-relaxed">
                {reason.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div
          className={`mt-16 text-center transition-all duration-700 delay-500 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-biodata-teal text-white font-medium hover:bg-biodata-teal/90 transition-all duration-300 shadow-lg shadow-biodata-teal/25 whitespace-nowrap"
          >
            <span>Agenda una consulta</span>
            <i className="ri-arrow-right-line" />
          </a>
        </div>
      </div>
    </section>
  );
}